import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, Trash2, Plus, X } from "lucide-react";

const CATEGORIES = [
  "Fundraising",
  "Investor Discovery",
  "Pitching",
  "Founder Life",
  "Capital Markets",
  "Term Sheets",
];

function slugify(s: string) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 80);
}

type Post = {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  category: string;
  read_time: string;
  linkedin_post: string | null;
  published_at: string;
};

const empty = {
  id: "",
  title: "",
  excerpt: "",
  content: "",
  category: CATEGORIES[3],
  linkedin_post: "",
};

export default function AdminBlog() {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(empty);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("blog_posts")
      .select("*")
      .order("published_at", { ascending: false });
    if (error) toast.error(error.message);
    setPosts((data as Post[]) ?? []);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const reset = () => setForm(empty);

  const edit = (p: Post) => {
    setForm({
      id: p.id,
      title: p.title,
      excerpt: p.excerpt,
      content: p.content,
      category: p.category,
      linkedin_post: p.linkedin_post ?? "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const save = async () => {
    if (!form.title.trim() || !form.excerpt.trim() || !form.content.trim()) {
      toast.error("Title, excerpt and content are required");
      return;
    }
    setSaving(true);
    const wordCount = form.content.split(/\s+/).length;
    const read_time = `${Math.max(2, Math.round(wordCount / 200))} min read`;

    if (form.id) {
      const { error } = await supabase
        .from("blog_posts")
        .update({
          title: form.title,
          excerpt: form.excerpt,
          content: form.content,
          category: form.category,
          linkedin_post: form.linkedin_post || null,
          read_time,
        })
        .eq("id", form.id);
      if (error) toast.error(error.message);
      else toast.success("Post updated");
    } else {
      const slug = `${slugify(form.title)}-${Date.now().toString(36)}`;
      const { error } = await supabase.from("blog_posts").insert({
        title: form.title,
        slug,
        excerpt: form.excerpt,
        content: form.content,
        category: form.category,
        linkedin_post: form.linkedin_post || null,
        author_name: "Aman Mahtani",
        read_time,
      });
      if (error) toast.error(error.message);
      else toast.success("Post published");
    }
    setSaving(false);
    reset();
    load();
  };

  const remove = async (id: string) => {
    if (!confirm("Delete this post?")) return;
    const { error } = await supabase.from("blog_posts").delete().eq("id", id);
    if (error) toast.error(error.message);
    else {
      toast.success("Deleted");
      load();
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="container mx-auto px-6 py-12 max-w-4xl">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-2xl font-display font-semibold">Blog composer</h1>
          {form.id && (
            <Button variant="outline" size="sm" onClick={reset} className="gap-2">
              <X className="w-4 h-4" /> Cancel edit
            </Button>
          )}
        </div>

        <div className="glass rounded-2xl p-6 space-y-4 mb-12">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-2">
              <Label className="mb-1.5 block">Title</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Week 3: pitching to angels in London"
              />
            </div>
            <div>
              <Label className="mb-1.5 block">Category</Label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <Label className="mb-1.5 block">Excerpt (1-2 sentences)</Label>
            <Textarea
              value={form.excerpt}
              onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
              rows={2}
              maxLength={220}
              placeholder="Quick summary that shows on the blog feed."
            />
          </div>

          <div>
            <Label className="mb-1.5 block">Content</Label>
            <Textarea
              value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
              rows={12}
              placeholder="The full post. Use blank lines between paragraphs."
            />
          </div>

          <div>
            <Label className="mb-1.5 block">LinkedIn version (optional)</Label>
            <Textarea
              value={form.linkedin_post}
              onChange={(e) => setForm({ ...form, linkedin_post: e.target.value })}
              rows={5}
              placeholder="Shorter punchy version for LinkedIn. Readers can copy it from the blog."
            />
          </div>

          <div className="flex justify-end">
            <Button onClick={save} disabled={saving} className="gap-2">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              {form.id ? "Update post" : "Publish post"}
            </Button>
          </div>
        </div>

        <h2 className="text-lg font-display font-semibold mb-4">Existing posts</h2>
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : posts.length === 0 ? (
          <p className="text-sm text-muted-foreground">No posts yet.</p>
        ) : (
          <div className="space-y-3">
            {posts.map((p) => (
              <div key={p.id} className="glass rounded-xl p-4 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-xs text-primary mb-0.5">{p.category}</div>
                  <div className="font-medium truncate">{p.title}</div>
                  <div className="text-xs text-muted-foreground">
                    {new Date(p.published_at).toLocaleDateString("en-GB")} · {p.read_time}
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => edit(p)}>Edit</Button>
                  <Button size="sm" variant="outline" onClick={() => remove(p.id)} className="text-destructive">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}